import { matchPath } from "react-router-dom";

export const HELP_REGISTRY = {
  search: {
    title: "Search",
    purpose: "Find companies or people quickly across your tenant records.",
    actions: ["Switch between Companies and Persons.", "Use quick filters for common compliance views.", "Open Advanced filters for detailed criteria, then use pagination."],
    howTo: ["Start with a broad term (name, registration number, or ID).", "Add filters one group at a time to narrow results.", "Open a result row to continue into company workflows."],
    watchOut: ["Too many filters can hide valid matches—clear chips and retry.", "Import and enrich actions update source data; use them intentionally."],
  },
  overview: {
    title: "Database Overview",
    purpose: "Monitor company portfolio health and open individual company records.",
    actions: ["Filter and sort by status, review date, and risk indicators.", "Expand rows for a richer operational snapshot.", "Open Company to manage documents, ownership, notes, and reviews."],
    howTo: ["Search or filter first, then expand a row for detail.", "Use status and review columns to prioritize work queues.", "Open Company when a case needs edits or formal review actions."],
    watchOut: ["Expanded rows are read-only summaries; edits happen on the Company page.", "Status and risk values are business-critical and appear in exports."],
  },
  intake: {
    title: "Intake",
    purpose: "Create a new client record through a guided onboarding workflow.",
    actions: ["Complete ENTITY details, then UBOs, then supporting documents.", "Use review step to validate before submission.", "Upload required files while capture fields are still fresh."],
    howTo: ["Move step-by-step in order to avoid missing dependencies.", "Treat UBO ownership percentages and dates as validation-sensitive fields.", "Submit only after review confirms required content is complete."],
    watchOut: ["Draft autosave helps, but incomplete required fields still block submission.", "Document kind/date mismatches can create compliance exceptions."],
  },
  review: {
    title: "Review",
    purpose: "Complete client risk assessment and formal review decisions.",
    actions: ["Capture review outcome, risk level, and rationale.", "Set next-review timing where applicable.", "Save decisions that drive portfolio risk reporting."],
    howTo: ["Use clear evidence-backed comments for auditability.", "Confirm supporting documents and ownership data are current before finalizing.", "Re-check dates and reviewer details before save."],
    watchOut: ["Review decisions influence compliance posture and reporting outputs.", "Avoid finalizing when prerequisite documentation is missing."],
  },
  reviewEdit: {
    title: "Edit Risk Review",
    purpose: "Update an existing risk review while preserving a clear audit trail.",
    actions: ["Adjust review outcome, rationale, and scheduling details.", "Save carefully when changes are final."],
    howTo: ["Confirm the company context before editing.", "Document why the review was changed in concise plain language."],
    watchOut: ["Review edits can affect risk monitoring and downstream reporting."],
  },
  export: {
    title: "Export",
    purpose: "Download operational workbooks for reporting and external handling.",
    actions: ["Generate the latest Final Updated Info workbook.", "Use export after key updates are complete.", "Share through approved internal channels."],
    howTo: ["Confirm source records are up to date first.", "Run export, then validate a few rows before distribution."],
    watchOut: ["Exports are snapshots—rerun after major updates.", "Treat downloaded files as sensitive compliance data."],
  },
  company: {
    title: "Company Record",
    purpose: "Manage a single company lifecycle across profile, governance, documents, and reviews.",
    actions: ["Use tabs to update core data, evidence, and history.", "Track status changes with effective dates and rationale.", "Coordinate reviews, notes, and remediation items."],
    howTo: ["Start in Overview to validate master details.", "Move to role, document, and review tabs based on your task.", "Use status controls carefully to reflect lifecycle events."],
    watchOut: ["Status changes can have compliance and reporting impact.", "Keep master/profile data consistent with submitted documents."],
  },
  "company.overview": {
    title: "Company • Overview",
    purpose: "Maintain master entity and profile information used across workflows.",
    actions: ["Edit legal identity, residency, and core profile fields.", "Validate values used by review and reporting modules."],
    howTo: ["Update official records first, then align profile attributes."],
    watchOut: ["Changes here affect downstream screens and exports."],
  },
  "company.roles": {
    title: "Company • Owners & Officers",
    purpose: "Manage control, ownership, and officer relationships.",
    actions: ["Add or update individuals and role periods.", "Confirm ownership percentages and relationship types."],
    howTo: ["Keep role dates and ownership shares internally consistent."],
    watchOut: ["Incomplete ownership data can block or weaken risk reviews."],
  },
  "company.documents": {
    title: "Company • Documents",
    purpose: "Store required compliance evidence and monitor document validity.",
    actions: ["Upload and classify records by document type.", "Track status, issue dates, and expiry windows.", "Address missing, expired, or rejected items."],
    howTo: ["Use the required-docs panel to close gaps first.", "Capture document issue date and expiry accurately to support automated checks."],
    watchOut: ["Some document date requirements expect items issued within the last 6 months."],
  },
  "company.addresses": {
    title: "Company • Addresses",
    purpose: "Maintain registered and operating location records.",
    actions: ["Add/update address entries with effective details."],
    howTo: ["Ensure address types and countries match source records."],
    watchOut: ["Address inconsistencies can affect review evidence quality."],
  },
  "company.bank": {
    title: "Company • Bank Accounts",
    purpose: "Track banking relationships used in compliance review context.",
    actions: ["Maintain account references and status details."],
    howTo: ["Use complete and current account metadata for clarity."],
    watchOut: ["Outdated bank info can create review and reporting issues."],
  },
  "company.notes": {
    title: "Company • Notes",
    purpose: "Capture operational context, follow-ups, and audit-friendly commentary.",
    actions: ["Log concise updates and decision context."],
    howTo: ["Write factual, time-relevant notes tied to actions."],
    watchOut: ["Avoid vague entries that cannot support audit review."],
  },
  "company.reviews": {
    title: "Company • Reviews",
    purpose: "View and manage review history and decision trail.",
    actions: ["Inspect prior outcomes and timing.", "Update or add reviews when reassessment is needed."],
    howTo: ["Cross-check with documents and ownership changes before new decisions."],
    watchOut: ["Review cadence and rationale should stay consistent with policy."],
  },
  "company.statusHistory": {
    title: "Company • Status History",
    purpose: "Audit status transitions over time.",
    actions: ["Review effective dates and change reasons.", "Validate lifecycle history before external reporting."],
    howTo: ["Use this tab when investigating timeline questions."],
    watchOut: ["Unexpected transitions may indicate process gaps."],
  },
  person: {
    title: "Person Record",
    purpose: "Manage an individual's identity, roles, documents, addresses, and notes used in compliance workflows.",
    actions: ["Use tabs to review profile, roles, documents, addresses, and notes.", "Update records to keep related company reviews accurate."],
    howTo: ["Validate identifying data first, then update supporting sections as needed."],
    watchOut: ["Person updates can impact ownership, screening, and review context across companies."],
  },
  portalOverview: {
    title: "Portal Overview",
    purpose: "Give tenant users a focused portfolio and action dashboard.",
    actions: ["Filter companies by risk, status, and attention flags.", "Open company records from a client-facing context."],
    howTo: ["Use attention filters first to prioritize urgent follow-up."],
    watchOut: ["Portal visibility can be role-dependent."],
  },
  portalCompany: {
    title: "Portal Company",
    purpose: "Review company status and documents from the portal workflow.",
    actions: ["Inspect document categories and expiry exposure.", "Upload and download documents when your role permits."],
    howTo: ["Start in Documents, then confirm status indicators and counts."],
    watchOut: ["Upload permissions vary by role and tenant policy."],
  },
  "portalCompany.documents": {
    title: "Portal Company • Documents",
    purpose: "Manage company document submissions and validity indicators in the portal.",
    actions: ["Upload documents with the correct type and issue date.", "Track status, expiry, and missing file gaps."],
    howTo: ["Use clear document labels and identifiers when available.", "Capture issue/expiry dates so review checks remain accurate."],
    watchOut: ["Document date requirements may require items issued within the last 6 months."],
  },
  "portalCompany.overview": {
    title: "Portal Company • Overview",
    purpose: "Read core company information and high-level metrics in a client-facing view.",
    actions: ["Review registration, status, and activity summary."],
    howTo: ["Use this tab for context before document actions."],
    watchOut: ["Editing permissions depend on your portal role."],
  },
  portalAdminDashboard: {
    title: "Portal Admin Dashboard",
    purpose: "Monitor tenant-level operational metrics and administrative health.",
    actions: ["Review key tenant indicators.", "Use admin links to manage invitations, members, and branding."],
    howTo: ["Treat this as an oversight page; perform changes in dedicated admin screens."],
    watchOut: ["Some controls are limited to owner/admin/super-admin roles."],
  },
  portalAdminInvite: {
    title: "Invite Users",
    purpose: "Invite tenant users with the right level of access.",
    actions: ["Send invitations to new users.", "Use role-appropriate assignments."],
    howTo: ["Invite only users who need access now and verify email accuracy."],
    watchOut: ["Invitations and roles affect access to sensitive compliance data."],
  },
  portalAdminMembers: {
    title: "Tenant Members",
    purpose: "Manage tenant membership and current access footprint.",
    actions: ["Review active users and roles.", "Adjust membership when staffing changes."],
    howTo: ["Periodically validate least-privilege access."],
    watchOut: ["Membership changes can immediately affect workflow continuity."],
  },
  portalAdminBranding: {
    title: "Tenant Branding",
    purpose: "Control tenant-specific branding for portal presentation.",
    actions: ["Update brand assets and display settings."],
    howTo: ["Preview changes to ensure readability in light/dark themes."],
    watchOut: ["Brand updates affect all tenant users in the portal."],
  },
  portalOpsTenants: {
    title: "Ops • Tenants",
    purpose: "Operate tenant lifecycle, seat allocation, and membership onboarding.",
    actions: ["Review tenant health and status.", "Manage seats, org linking, and invitations."],
    howTo: ["Address health warnings first, then complete membership setup."],
    watchOut: ["Ops actions can affect tenant authentication and access immediately."],
  },
  portalOpsAudit: {
    title: "Ops • Audit Log",
    purpose: "Review system activity and impersonation events for operational control.",
    actions: ["Filter recent audit entries.", "Start/stop impersonation with strict controls."],
    howTo: ["Use tenant and limit filters to investigate targeted events."],
    watchOut: ["Impersonation should be time-bound and auditable."],
  },
  portalOpsTenantMembers: {
    title: "Ops • Tenant Members",
    purpose: "Inspect and manage members for a specific tenant in operations mode.",
    actions: ["Review member status and access data.", "Support tenant-level user administration."],
    howTo: ["Confirm tenant context before applying changes."],
    watchOut: ["Member changes may impact active sessions and support tickets."],
  },
};

export function resolveHelpKey(location) {
  const { pathname, search } = location;
  const params = new URLSearchParams(search);

  if (matchPath("/company/:id", pathname)) {
    const rawTab = params.get("tab") || "overview";
    const tabAliases = {
      "status-history": "statusHistory",
      status_history: "statusHistory",
    };
    const normalizedTab = tabAliases[rawTab] || rawTab;
    const tabKey = `company.${normalizedTab}`;
    if (HELP_REGISTRY[tabKey]) return tabKey;
    return "company";
  }

  if (matchPath("/person/:id", pathname)) return "person";
  if (matchPath("/reviews/:id/edit", pathname)) return "reviewEdit";

  if (matchPath("/portal/company/:id", pathname)) {
    const rawTab = params.get("tab") || "documents";
    const tabAliases = {
      docs: "documents",
      document: "documents",
    };
    const normalizedTab = tabAliases[rawTab] || rawTab;
    const tabKey = `portalCompany.${normalizedTab}`;
    if (HELP_REGISTRY[tabKey]) return tabKey;
    return "portalCompany";
  }
  if (matchPath("/portal/overview", pathname)) return "portalOverview";
  if (matchPath("/portal/admin/dashboard", pathname)) return "portalAdminDashboard";
  if (matchPath("/portal/admin/invite", pathname)) return "portalAdminInvite";
  if (matchPath("/portal/admin/members", pathname)) return "portalAdminMembers";
  if (matchPath("/portal/admin/branding", pathname)) return "portalAdminBranding";
  if (matchPath("/portal/ops/tenants/:tenantId/members", pathname)) return "portalOpsTenantMembers";
  if (matchPath("/portal/ops/tenants", pathname)) return "portalOpsTenants";
  if (matchPath("/portal/ops/audit", pathname)) return "portalOpsAudit";

  const staticRoutes = {
    "/": "search",
    "/overview": "overview",
    "/intake": "intake",
    "/review": "review",
    "/export": "export",
  };

  if (pathname === "/ops/tenants" || pathname === "/ops/audit") {
    // These routes immediately redirect to portal routes where contextual help is shown.
    return null;
  }

  return staticRoutes[pathname] || null;
}
